import { lazy, Suspense, useState } from "react";
import {
  Map,
  BarChart3,
  TrendingUp,
  Layers,
  Grid3x3,
  Building2,
  Trophy,
} from "lucide-react";

import { useTdmFilters } from "@/hooks/useTdmFilters";
import { useTdmStats, useTdmContractings } from "@/hooks/queries/useTdmStats";
import { Skeleton } from "@/components/ui/skeleton";

import { TdmFilterBar } from "@/components/tdm/TdmFilterBar";
import { TdmKpis } from "@/components/tdm/TdmKpis";
import { TopObras } from "@/components/tdm/TopObras";
import { TdmInfoButton } from "@/components/tdm/TdmInfoButton";
import { TdmDrilldownModal } from "@/components/tdm/TdmDrilldownModal";
import { TDM_CATEGORY_MAP } from "@/lib/tdm-constants";
import type { TdmCategory, TdmDrill, TdmSector } from "@/types/tdm";

// Recharts + mapa son pesados: fuera del bundle principal.
const PeruMap = lazy(() =>
  import("@/components/tdm/PeruMap").then((m) => ({ default: m.PeruMap })),
);
const DepartmentRanking = lazy(() =>
  import("@/components/tdm/DepartmentRanking").then((m) => ({
    default: m.DepartmentRanking,
  })),
);
const QuarterlyTrend = lazy(() =>
  import("@/components/tdm/QuarterlyTrend").then((m) => ({
    default: m.QuarterlyTrend,
  })),
);
const CategorySectorMatrix = lazy(() =>
  import("@/components/tdm/CategorySectorMatrix").then((m) => ({
    default: m.CategorySectorMatrix,
  })),
);
const CategoryShare = lazy(() =>
  import("@/components/tdm/CategoryShare").then((m) => ({
    default: m.CategoryShare,
  })),
);
const TopEntities = lazy(() =>
  import("@/components/tdm/TopEntities").then((m) => ({
    default: m.TopEntities,
  })),
);

function Section({
  title,
  icon,
  children,
  className = "",
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`flex flex-col rounded-2xl bg-card p-5 shadow-sm ${className}`}
    >
      <div className="mb-4 flex items-center gap-2">
        <span className="text-muted-foreground">{icon}</span>
        <h3 className="text-sm font-bold uppercase tracking-wide text-muted-foreground">
          {title}
        </h3>
      </div>
      <div className="flex min-h-0 flex-1 flex-col justify-center">
        {children}
      </div>
    </div>
  );
}

const chartFallback = <Skeleton className="h-64 w-full rounded-xl" />;

export default function Tdm() {
  const { filters, patch, reset } = useTdmFilters();
  const { data: stats, isLoading } = useTdmStats(filters);

  const [drill, setDrill] = useState<TdmDrill | null>(null);

  /**
   * Único sitio donde vive la regla de métrica: en Cantidad el mapa y el ranking
   * pintan solo la categoría activa, así que el corte se acota a ella y se ordena
   * por su cantidad convertida. En Monto el número es la suma de las tres y el
   * corte no se acota, salvo que el clic ya traiga categoría propia.
   */
  const abrirDrill = (d: TdmDrill) => {
    if (filters.metric !== "quantity") return setDrill(d);
    const cat = d.categories?.[0] ?? filters.activeCategory;
    setDrill({ ...d, categories: [cat], qtyCategory: cat });
  };

  const drillDepartamento = (department: string) =>
    abrirDrill({
      title: department,
      subtitle: "Obras del departamento",
      departmentNames: [department],
    });
  const drillCategoria = (categoria: TdmCategory) =>
    abrirDrill({
      title: TDM_CATEGORY_MAP[categoria].label,
      subtitle: "Obras de la categoría",
      categories: [categoria],
    });

  // Top 3 obras por potencial de venta.
  const { data: topObras, isLoading: obrasLoading } = useTdmContractings(
    filters,
    0,
    3,
  );

  return (
    <div className="mx-auto w-full flex-1 px-4 py-6 md:px-14">
      {/* Encabezado */}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">
            Inteligencia Comercial
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Análisis del mercado por territorio a partir de los recursos de{" "}
            <span className="font-semibold text-foreground/80">
              Materiales y Subcontratos
            </span>{" "}
            que coinciden con tu búsqueda. Filtra por monto (potencial de venta)
            o por cantidad, categorizado en Diluido, Sólido y Emulsión.
          </p>
        </div>
        <TdmInfoButton />
      </div>

      <div className="space-y-6">
        <TdmFilterBar filters={filters} patch={patch} onReset={reset} />

        <TdmKpis data={stats} isLoading={isLoading} />

        {/* Mapa + share/matriz */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Section
            title="Distribución territorial"
            icon={<Map className="size-4" />}
            className="lg:col-span-2"
          >
            <Suspense fallback={chartFallback}>
              <PeruMap
                data={stats}
                metric={filters.metric}
                activeCategory={filters.activeCategory}
                onDrill={drillDepartamento}
              />
            </Suspense>
          </Section>

          <div className="flex flex-col gap-6">
            <Section
              title="Share por categoría"
              icon={<Layers className="size-4" />}
            >
              <Suspense fallback={chartFallback}>
                <CategoryShare
                  data={stats}
                  metric={filters.metric}
                  onDrill={drillCategoria}
                />
              </Suspense>
            </Section>
            <Section
              title="Categoría × Sector"
              icon={<Grid3x3 className="size-4" />}
              className="flex-1"
            >
              <Suspense fallback={chartFallback}>
                <CategorySectorMatrix
                  data={stats}
                  metric={filters.metric}
                  onDrill={(categoria: TdmCategory, sector: TdmSector) =>
                    abrirDrill({
                      title: `${TDM_CATEGORY_MAP[categoria].label} · ${sector.charAt(0)}${sector.slice(1).toLowerCase()}`,
                      subtitle: "Obras de la categoría en ese sector",
                      categories: [categoria],
                      sectors: [sector],
                    })
                  }
                />
              </Suspense>
            </Section>
          </div>
        </div>

        {/* Ranking + tendencia */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Section
            title="Ranking por departamento"
            icon={<BarChart3 className="size-4" />}
          >
            <Suspense fallback={chartFallback}>
              <DepartmentRanking
                data={stats}
                metric={filters.metric}
                activeCategory={filters.activeCategory}
                onDrill={drillDepartamento}
              />
            </Suspense>
          </Section>
          <Section
            title="Tendencia trimestral"
            icon={<TrendingUp className="size-4" />}
          >
            <Suspense fallback={chartFallback}>
              <QuarterlyTrend
                data={stats}
                metric={filters.metric}
                activeCategory={filters.activeCategory}
                onDrill={(quarter: string, categoria: TdmCategory) =>
                  abrirDrill({
                    title: `${quarter} · ${TDM_CATEGORY_MAP[categoria].label}`,
                    subtitle: "Obras publicadas ese trimestre",
                    quarter,
                    categories: [categoria],
                  })
                }
              />
            </Suspense>
          </Section>
        </div>

        {/* Top entidades */}
        <Section
          title="Top entidades compradoras"
          icon={<Building2 className="size-4" />}
        >
          <Suspense fallback={chartFallback}>
            <TopEntities
              data={stats}
              metric={filters.metric}
              activeCategory={filters.activeCategory}
            />
          </Suspense>
        </Section>

        {/* Top 3 obras por potencial de venta */}
        <Section
          title="Top 3 obras · mayor potencial"
          icon={<Trophy className="size-4" />}
        >
          <TopObras rows={topObras?.rows ?? []} isLoading={obrasLoading} />
        </Section>
      </div>

      <TdmDrilldownModal
        drill={drill}
        filters={filters}
        onClose={() => setDrill(null)}
      />
    </div>
  );
}
